import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import useAssignmentChatThread from 'hooks/chat/useAssignmentChatThread';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import chatService from 'services/chat';
import { getUserPreferredFullName } from 'utils/helper';

/**
 * Role-specific thread setup for instructors viewing a student's submission.
 *
 * Use from instructor submission chat panels. It resolves the student's existing assignment
 * chat instead of creating one for the instructor account, derives the instructor header,
 * disables suggestions, and enables periodic reconciliation while the panel is open.
 *
 * @param {Object} props
 * @param {Object|null} props.currentSubmission Submission whose owner chat should be loaded.
 * @param {boolean} props.isOpen Whether the chat panel is mounted/open.
 * @returns {Object} `useAssignmentChatThread` result plus `fileKey` and instructor `viewModel`.
 */
const useInstructorSubmissionChat = ({ currentSubmission, isOpen }) => {
    const { currentAssignment, studentAccountUsers } = useApp();
    const { auth } = useAuth();
    const { t } = useTranslation('assignment');

    const currentStudent = useMemo(() => {
        return studentAccountUsers?.find(
            ({ owner_account_id }) => owner_account_id === currentSubmission?.owner_account_id
        );
    }, [currentSubmission?.owner_account_id, studentAccountUsers]);

    const studentName = getUserPreferredFullName(currentStudent) || t('common:ui.student', 'Student');
    const viewModel = useMemo(
        () => ({
            header: {
                title: t('formative-feedback.student-inquiry-title', '{studentName} Feedback Inquiry', {
                    studentName,
                }),
            },
        }),
        [studentName, t]
    );
    const fileKey = currentSubmission?.files?.[0]?.key;

    const resolveChat = useCallback(async () => {
        if (!currentAssignment?.id || !auth?.course_id || !currentSubmission?.owner_account_id) return null;

        return chatService.getAssignmentChatSummary({
            assignment_id: currentAssignment.id,
            course_id: auth.course_id,
            owner_account_id: currentSubmission.owner_account_id,
        });
    }, [auth?.course_id, currentAssignment?.id, currentSubmission?.owner_account_id]);

    const assignmentChatThread = useAssignmentChatThread({
        assignmentId: currentAssignment?.id,
        courseId: auth?.course_id,
        ownerAccountId: currentSubmission?.owner_account_id,
        readerAccountId: auth?.owner_account_id || auth?.id,
        resolveChat,
        isOpen,
        refreshIntervalMs: 10 * 1000,
        shouldShowSuggestions: false,
    });

    return {
        ...assignmentChatThread,
        fileKey,
        viewModel,
        assistantIdentity: {
            icon: 'hugo',
            label: t('chat:hugo.title', 'Hugo'),
        },
    };
};

export default useInstructorSubmissionChat;
